import {
  FrequencyUnit,
  HabitSchedule,
  Timeframe,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';

import { weekdaysNumberMap } from '../constants';
import { getScheduleFrequency } from '../get-schedule-frequency';
import { getTimeframeDayEntries } from '../get-timeframe-day-entries';
import { getTimeframeOccurrences } from '../get-timeframe-occurrences';
import { isBetween } from '../lib/date-fns';
import { getActiveSchedule } from './get-active-schedule';
import { isEveryDayRrule } from './is-every-day-rrule';

const frequencySlotFuncsMap = {
  [FrequencyUnit.Week]: {
    startOfSlot: startOfWeek,
    endOfSlot: endOfWeek,
    eachOfInterval: eachWeekOfInterval,
  },
  [FrequencyUnit.Month]: {
    startOfSlot: startOfMonth,
    endOfSlot: endOfMonth,
    eachOfInterval: eachMonthOfInterval,
  },
  [FrequencyUnit.Year]: {
    startOfSlot: startOfYear,
    endOfSlot: endOfYear,
    eachOfInterval: eachYearOfInterval,
  },
};

export type GetSchedulesPerfectSlotsOptions = {
  startDate: Timestamp;
  endDate: Timestamp;
  weekStartsOn: WeekDays;
  today: Timestamp;
  /** Stay inside the given range instead of widening it to cover every resolved entry. */
  strictDates?: boolean;
};

// The week/month/year slots in which the habit met its target — what the calendar paints as a
// "perfect" timeframe.
export const getSchedulesPerfectSlots = (
  schedules: HabitSchedule[],
  {
    startDate,
    endDate,
    weekStartsOn,
    today,
    strictDates,
  }: GetSchedulesPerfectSlotsOptions,
): Timeframe[] => {
  const activeSchedule = getActiveSchedule(schedules, today);
  if (!activeSchedule) {
    return [];
  }

  const { dateEntries } = getTimeframeDayEntries(schedules, {
    startDate,
    endDate,
  });
  const { occurrences } = getTimeframeOccurrences(schedules, {
    startDate,
    endDate,
  });

  // A goal-template schedule reaches this without passing the task converter, so an unknown unit is still
  // possible here; a missing key would be destructured and throw.
  const { startOfSlot, endOfSlot, eachOfInterval } =
    frequencySlotFuncsMap[getScheduleFrequency(activeSchedule)] ??
    frequencySlotFuncsMap[FrequencyUnit.Week];

  // Every-day habits cap a day at its target so one big day cannot cover a missed one; times-per-week
  // keeps the weekly sum.
  const dailyTarget = activeSchedule.completionTarget.count;
  const isEveryDayHabit =
    !activeSchedule.frequency && isEveryDayRrule(activeSchedule.rrule.format);
  const dateFnsOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };

  const entryDateTimes = dateEntries.map(({ date }) => date.getTime());

  // dateEntries can fall outside the range, so widen it to cover them.
  const rangeStart = strictDates
    ? startDate
    : new Date(Math.min(startDate.getTime(), ...entryDateTimes));
  const rangeEnd = strictDates
    ? endDate
    : new Date(Math.max(endDate.getTime(), ...entryDateTimes));

  return eachOfInterval(
    {
      start: startOfSlot(rangeStart, dateFnsOptions),
      end: endOfSlot(rangeEnd, dateFnsOptions),
    },
    dateFnsOptions,
  ).reduce<Timeframe[]>((acc, slotStartDate) => {
    const slotEndDate = endOfSlot(slotStartDate, dateFnsOptions);
    const completionCount = dateEntries.reduce((count, entry) => {
      if (!entry.value || !isBetween(entry.date, slotStartDate, slotEndDate)) {
        return count;
      }
      return (
        count +
        (isEveryDayHabit ? Math.min(entry.value, dailyTarget) : entry.value)
      );
    }, 0);
    const slotTarget =
      activeSchedule.frequency?.count ??
      (occurrences.filter((occurrence) =>
        isBetween(occurrence, slotStartDate, slotEndDate),
      ).length * activeSchedule.completionTarget.count ||
        1);

    if (slotTarget >= 1 && completionCount >= slotTarget) {
      acc.push({
        referenceDate: slotStartDate,
        startDate: slotStartDate,
        endDate: slotEndDate,
      });
    }

    return acc;
  }, []);
};
